import { Group } from 'fabric';
import { assembleSpecs } from '../assemble';
import { leaderLine } from '../kits/calloutKit';
import type { LeaderLineOptions } from '../kits/calloutKit';
import type { TextMeasurer, TextSpec } from '../kits/specs';
import { estimateMeasure } from '../kits/specs';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ToolRecipe } from '../types';

/**
 * Label with arrow — a caption at one end and a leader line that points at
 * whatever the teacher is labeling (CATALOG, calloutKit.leaderLine). The
 * label text sits at the TAIL end; the arrowhead lands at the anchor end.
 *
 * from/to are recipe-INTERNAL geometry: the leader is a fixed length at a
 * pleasant downward-right angle, built once. The teacher then repositions and
 * rotates the whole group to aim it — so the recipe never needs anchor
 * coordinates, and the object is trivially re-usable anywhere on the poster.
 *
 * The caption carries data.tableCell = {kind:'param', key:'text'} (stamped by
 * assemble.ts), so double-clicking it on canvas edits the label in place and
 * regenerates — one history entry, transform preserved.
 *
 * Emits FabricText + Line + Path (arrowhead) only, grouped and tagged
 * {tool,version,params} — plain fabric JSON, vector at any export dpi.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the handwriting.ts pattern) — params arrive
// untrusted (user-typed or replayed from stored JSON); generate() must never
// throw.
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty label is MEANINGFUL (= a bare pointer, no caption). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

// ---------------------------------------------------------------------------
// Text measurement — canvas TextMetrics when available (the insert/Apply paths
// ensureFont the family first, so the measure sees the real face), estimate
// fallback elsewhere (SSR/node safety). Same helper as fraction.ts.
// ---------------------------------------------------------------------------

function makeMeasurer(family: string): TextMeasurer {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return estimateMeasure;
    return (text, fontSize) => {
      ctx.font = `${fontSize}px "${family}", sans-serif`;
      const w = ctx.measureText(text).width;
      return Number.isFinite(w) && w >= 0 ? w : estimateMeasure(text, fontSize);
    };
  } catch {
    return estimateMeasure;
  }
}

const INK = '#1e293b';
const DEFAULT_FONT = 'Arial';

const leaderLabel: ToolRecipe = {
  id: 'leader-label',
  domain: 'science',
  tags: ['label', 'callout', 'leader-line', 'arrow', 'diagram', 'annotation'],
  name: 'Label with arrow',
  category: 'callouts',
  version: 1,
  description:
    'A caption with a leader line and arrowhead pointing at what it labels. Double-click the text to rename it, then rotate the whole label to aim the arrow.',
  defaults: {
    text: 'Label',
    elbow: 'auto',
    arrowSize: 18,
    color: INK,
    fontFamily: DEFAULT_FONT,
    fontSize: 48,
  },
  schema: [
    { key: 'text', label: 'Label text', type: 'text', help: 'Leave empty for a bare pointer.' },
    {
      key: 'elbow',
      label: 'Leader style',
      type: 'select',
      options: [
        { value: 'auto', label: 'Bent (elbow)' },
        { value: 'none', label: 'Straight' },
      ],
      section: 'Style',
    },
    {
      key: 'arrowSize',
      label: 'Arrow size',
      type: 'number',
      min: 6,
      max: 80,
      step: 1,
      section: 'Style',
    },
    { key: 'color', label: 'Color', type: 'color', section: 'Style' },
    { key: 'fontFamily', label: 'Font', type: 'font', section: 'Style' },
    {
      key: 'fontSize',
      label: 'Text size',
      type: 'number',
      min: 8,
      max: 240,
      step: 1,
      section: 'Style',
    },
  ],
  async generate(params) {
    const text = rawStr(params.text, 'Label');
    const elbow: LeaderLineOptions['elbow'] = params.elbow === 'none' ? 'none' : 'auto';
    const arrowSize = clamp(num(params.arrowSize, 18), 4, 120);
    const color = str(params.color, INK);
    const family = str(params.fontFamily, DEFAULT_FONT);
    const fontSize = clamp(num(params.fontSize, 48), 6, 400);
    const strokeWidth = clamp(Math.round(arrowSize * 0.34), 2, 40);

    // Caption at the tail end: left/vertically-centered at the local origin so
    // the leader can start just past its measured right edge.
    const label: TextSpec = {
      kind: 'text',
      text,
      x: 0,
      y: 0,
      fontSize,
      fontFamily: family,
      fill: color,
      originX: 'left',
      originY: 'center',
      param: 'text',
    };

    const measure = makeMeasurer(family);
    const textWidth = text ? measure(text, fontSize) : 0;
    const gap = fontSize * 0.35;
    const from = { x: textWidth + gap, y: 0 };
    // Fixed, pleasant downward-right reach; the teacher rotates to aim it.
    const to = { x: from.x + fontSize * 3.2, y: from.y + fontSize * 2.0 };

    const leader = leaderLine({ from, to, elbow, color, strokeWidth, headSize: arrowSize });

    const group = new Group(assembleSpecs([label, ...leader.specs]));
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, leaderLabel, params);
  },
};

registerRecipe(leaderLabel);
